import { Link } from "react-router-dom";
import { ExchangeMark, EXCHANGES, DATA_PAGE } from "./ExchangeLogos";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { accountUrl } from "@/lib/account";
import { Heading, Lead, Chips, Cmd } from "./HeroCopy";
import { ease } from "@/components/landing/primitives";
import { ALPHA_URL, VERIFY_URL, useVerifyStats, imgSize } from "./shared";
import { SetupsMock, ResearchMock, NewsMock } from "./Mocks";
import { HeroBackground } from "./HeroBackground";

const TABS = [
  { k: "F1", id: "setups", label: "Setups", url: ALPHA_URL, cap: "A-grade buy & sell setups from 10+ strategies, every one published to Verify.", el: <SetupsMock /> },
  { k: "F2", id: "flow", label: "Order Flow", url: `${ALPHA_URL}/terminal`, cap: "Footprint, Smart Tape, CVD and the Hyperliquid whale layer. Free today.", img: "/of/footprint.webp" },
  { k: "F3", id: "pulse", label: "Pulse", url: `${ALPHA_URL}/screener`, cap: "Market Pulse screener: unusual volume and compression before the breakout.", img: "/of/pulse.webp" },
  { k: "F4", id: "research", label: "Research", url: ALPHA_URL, cap: "Deep research scorecards that answer one question: is this asset worth it?", el: <ResearchMock /> },
  { k: "F5", id: "news", label: "News", url: ALPHA_URL, cap: "Breaking news, ranked by impact, with the price reaction measured live.", el: <NewsMock /> },
];

const VerifyChip = () => {
  const v = useVerifyStats();
  const live = v?.live && v.wr04 != null;
  return (
    <a href={VERIFY_URL} data-testid="hero-verify-chip" className="group inline-flex items-center gap-2.5 font-mono text-[10px] leading-relaxed sm:text-[11px] text-[#9DA3AE] transition-colors hover:text-[#ECEBE4]">
      <ShieldCheck size={14} className="text-[#BACD1C]/80" />
      {live ? (
        <span><span className="text-[#ECEBE4]">{v.wr04.toFixed(1)}%</span> A-grade · <span className="text-[#ECEBE4]">{v.resolved?.toLocaleString()}</span> scanned setups · live at verify</span>
      ) : (
        <span>Every setup published · live at verify</span>
      )}
      <ArrowUpRight size={12} className="opacity-50 transition-opacity group-hover:opacity-100" />
    </a>
  );
};

const Zoom = ({ t }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button type="button" data-testid={`hero-zoom-${t.id}`} aria-label={`Expand ${t.label} screenshot`}
        className="absolute bottom-3 right-3 flex items-center gap-1.5 border border-white/15 bg-black/70 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[#ECEBE4] backdrop-blur-md transition-colors duration-300 hover:border-white/40">
        <Maximize2 size={12} /> Expand
      </button>
    </DialogTrigger>
    <DialogContent data-testid="hero-zoom-dialog" className="max-w-[96vw] gap-0 border-white/10 bg-[#0B0C0F] p-0 sm:max-w-[92vw]">
      <DialogTitle className="border-b border-white/[0.07] px-4 py-3 font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-[#9DA3AE]"><span className="text-[#FFB224]">{t.k}</span> · {t.label}</DialogTitle>
      <div className="max-h-[80vh] overflow-auto overscroll-contain" data-lenis-prevent>
        <img src={t.img} alt={`TradrQuant ${t.label} terminal, full size`} {...imgSize(t.img)} className="block h-auto w-[220%] max-w-none sm:w-full" />
      </div>
      <p className="border-t border-white/[0.07] px-4 py-2 font-mono text-[10px] text-[#5A6170] sm:hidden">Swipe to pan the screenshot</p>
    </DialogContent>
  </Dialog>
);

const Panels = ({ i, pick }) => {
  const refs = useRef([]);
  const touch = useRef(null);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
    if (Math.abs(dx) > 50) pick((i + (dx < 0 ? 1 : TABS.length - 1)) % TABS.length);
  };
  const [h, setH] = useState(null);
  useEffect(() => {
    const el = refs.current[i];
    if (!el) return;
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [i]);
  return (
    <div data-testid="hero-panels" className="relative overflow-hidden transition-[height] duration-500 ease-out" style={{ height: h ?? undefined }}
      onTouchStart={(e) => (touch.current = e.touches[0].clientX)} onTouchEnd={onTouchEnd}>
      {TABS.map((t, n) => (
        <div key={t.id} ref={(el) => (refs.current[n] = el)} aria-hidden={n !== i} data-testid={n === i ? `fkey-panel-${t.id}` : undefined}
          className={`absolute inset-x-0 top-0 transition-opacity duration-500 ${n === i ? "opacity-100" : "pointer-events-none opacity-0"} ${h == null && n === i ? "!relative" : ""}`}>
          {t.img
            ? <div className="relative"><img src={t.img} alt={`TradrQuant ${t.label} terminal`} {...imgSize(t.img)} className="block h-auto w-full" /><Zoom t={t} /></div>
            : <div className="h-[420px] overflow-hidden p-4 sm:h-[540px] sm:p-8 lg:h-[560px]">{t.el}</div>}
        </div>
      ))}
    </div>
  );
};

const TerminalFrame = ({ i, pick }) => {
  const tab = TABS[i];
  return (
    <motion.div id="hero-terminal" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.6, ease }} className="mt-8 min-w-0 scroll-mt-28 sm:mt-14" data-testid="hero-terminal">
      <div className="border border-white/[0.10] bg-[#0B0C0F] shadow-[0_60px_140px_-50px_rgba(0,0,0,1)]">
        <div className="flex items-center gap-2 border-b border-white/[0.07] px-4 py-2.5">
          <span className="h-2 w-2 rounded-full bg-[#FF4D5A]/70" /><span className="h-2 w-2 rounded-full bg-white/35" /><span className="h-2 w-2 rounded-full bg-[#BACD1C]/70" />
          <span className="ml-3 truncate font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]"><span className="hidden sm:inline">tradrquant terminal · </span><span className="text-[#FFB224] sm:hidden">{tab.k} </span>{tab.label}</span>
          <span className="ml-3 font-mono text-[9px] text-[#5A6170] sm:hidden" data-testid="hero-tab-index">{i + 1}/{TABS.length}</span>
          <span className="ml-auto flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#5A6170]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#BACD1C]" />live</span>
        </div>
        <div role="tablist" className="hidden overflow-x-auto border-b border-white/[0.07] sm:flex">
          {TABS.map((t, n) => (
            <button key={t.id} role="tab" aria-selected={n === i} data-testid={`fkey-${t.id}`} onClick={() => pick(n)}
              className={`relative flex shrink-0 items-center gap-2 border-r border-white/[0.06] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] transition-colors sm:px-6 ${n === i ? "bg-white/[0.04] text-[#ECEBE4]" : "text-[#5A6170] hover:text-[#9DA3AE]"}`}>
              <span className={n === i ? "text-[#FFB224]" : "text-white/25"}>{t.k}</span>{t.label}
              {n === i && <motion.span layoutId="fkey-bar" className="absolute inset-x-0 bottom-0 h-px bg-[#ECEBE4]" />}
            </button>
          ))}
        </div>
        <Panels i={i} pick={pick} />
        <div data-testid="hero-status-bar" className="flex flex-col gap-2 border-t border-white/[0.07] bg-white/[0.015] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className="font-mono text-[11px] leading-relaxed text-[#9DA3AE]"><span className="text-[#FFB224]">{tab.k}</span> · {tab.cap}</p>
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-1.5 sm:hidden" aria-hidden="true">
              {TABS.map((t, n) => <span key={t.id} className={`h-1 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-5 bg-[#FFB224]" : "w-1.5 bg-white/20"}`} />)}
            </div>
            <a href={tab.url} data-testid="hero-open-live" className="group inline-flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#ECEBE4] transition-colors duration-300 hover:text-[#BACD1C]">
              Open live <ArrowUpRight size={12} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ExchangeStrip = () => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} data-testid="hero-exchange-strip"
    className="mt-8 border-y border-white/[0.06] py-3 font-mono text-[10px] uppercase tracking-[0.16em] sm:mt-9 sm:flex sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-2 sm:text-[11px] sm:tracking-[0.18em]">
    <span className="flex items-center gap-2 text-[#7D8390]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#BACD1C]" />Low-latency feeds<span className="text-[#5A6170] sm:hidden">· 5 venues</span></span>
    <div className="mt-3 flex items-center justify-between sm:mt-0 sm:contents">
      {EXCHANGES.map((x) => (
        <Link key={x.id} to={`${DATA_PAGE}#${x.id}`} title={`${x.name} real-time data`} aria-label={x.name} data-testid={`hero-exchange-${x.id}`}
          className="flex items-center gap-2 text-[#7D8390] grayscale transition-colors duration-300 hover:text-[#ECEBE4]">
          <ExchangeMark id={x.id} className={x.id === "bybit" ? "h-3" : "h-4 sm:h-3.5"} />
          {x.id !== "bybit" && <span className="hidden sm:inline">{x.name}</span>}
        </Link>
      ))}
    </div>
    <span className="hidden text-[#5A6170] lg:inline">+ more · real-time trades, books, OI, funding, liquidations</span>
  </motion.div>
);

export const HeroTerminal = () => {
  const [i, setI] = useState(0);
  const pick = setI;
  const pickAndShow = (n) => { pick(n); document.getElementById("hero-terminal")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || /input|textarea|select/i.test(e.target.tagName)) return;
      const m = /^F([1-5])$/.exec(e.key);
      if (!m) return;
      e.preventDefault();
      pickAndShow(Number(m[1]) - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section id="top" data-testid="hero-section" className="relative mx-2 mt-[116px] overflow-hidden md:mt-[124px] rounded-2xl border border-white/[0.07] bg-[#08090B] pt-14 sm:mx-3 md:mx-5 md:pt-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[980px]" data-testid="hero-bg">
        <HeroBackground variant="both" intensity={0.32} />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[#08090B]" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-5 pb-10 sm:px-6 sm:pb-20 md:px-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} data-testid="hero-release-badge" className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#7D8390]">
          TradrQuant · institutional market intelligence
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.3 }} className="mt-6"><Heading /></motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}><Lead className="mt-5" /></motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-7 flex max-w-4xl flex-col gap-3 border-t border-white/[0.07] pt-4 md:flex-row md:items-center md:justify-between">
          <Chips active={i} onPick={pickAndShow} />
          <Cmd className="text-[11px] text-[#7D8390]" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a href={ALPHA_URL} data-testid="hero-primary-cta" className="group inline-flex items-center justify-center gap-2 bg-[#BACD1C] px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-black transition-[background-color,transform] duration-300 hover:bg-[#ECEBE4] active:scale-[0.98]">
            Open free terminal <ArrowUpRight size={17} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
          <a href={accountUrl()} data-testid="hero-secondary-cta" className="inline-flex items-center justify-center border border-white/20 px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-[#ECEBE4] transition-[border-color,background-color] duration-300 hover:border-white/50 hover:bg-white/[0.04]">
            Create free account
          </a>
          <div className="sm:ml-4"><VerifyChip /></div>
        </motion.div>
        <ExchangeStrip />
        <TerminalFrame i={i} pick={pick} />
      </div>
    </section>
  );
};
